export type ID = string;
export type DateString = string;
export type LoadingState = boolean;
export type ErrorState = Error | string | null | { error: string };

// Generic types for flexibility
export type GenericObject<T = unknown> = Record<string, T>;
export type Nullable<T> = T | null;
export type Optional<T> = T | undefined;

// Common data types
export interface SelectOption {
  label: string;
  value: string;
  id?: ID;
}

export interface Announcement {
  id: ID;
  title: string;
  content: string;
  date: DateString;
  priority?: string;
}

export interface CrewRate {
  id: ID;
  rate: number;
  crewType: string;
  effectiveDate: DateString;
}

export interface Job {
  _id: ID;
  jobNumber: string;
  customerId: ID;
  status: string;
  date: DateString;
  service_type: string;
}

export interface Alert {
  id: ID;
  type: string;
  message: string;
  severity: string;
  timestamp: DateString;
}

export interface Warning {
  id: ID;
  type: string;
  message: string;
  jobId?: ID;
}

export interface PaymentData {
  id: ID;
  amount: number;
  date: DateString;
  method: string;
  status: string;
}

export interface HistoryItem {
  id: ID;
  action: string;
  timestamp: DateString;
  userId: ID;
  details: GenericObject;
}

// Common entity types
export interface Branch {
  label: string;
  branchId: ID;
  // camel case and capital, how can both be present?
  dispatch_start_time?: string;
  dispatchTime?: string;
  name?: string;
  _id?: ID;
}

export interface Customer {
  _id?: ID;
  first_name?: string;
  last_name?: string;
  fullName?: string;
  name?: string;
  photo_url?: string;
  email?: string;
  phone?: string;
  phone_number?: string;
  designation?: string;
  info?: {
    branch?: {
      name?: string;
    };
  };
}

export interface MoveSize {
  type?: string;
  value?: string;
}

export interface Info {
  elevator?: boolean;
  stairs?: boolean;
  lc?: string | number;
  sqft?: string | number;
  floors?: string | number;
  cube?: string | number;
  move_size?: MoveSize;
}

export interface Prices {
  driver?: number[];
  helper?: number[];
  truck?: number[];
}

export interface UpSellAndDownSellPrices {
  down_sell_price: Prices;
  up_sell_price: Prices;
}

// User State Types
export interface UserState {
  userInfo: Nullable<Customer>;
  domain: string;
  token: Nullable<string>;
  userStoreLoading: LoadingState;
  currentBranch: Nullable<Branch>;
  upSellDownSellPrice?: UpSellAndDownSellPrices;
  branchListNew: Branch[];
  crewRatesData: CrewRate[];
  crewRatesLoading: boolean;
  zipCodeValue: string;
  version: string;
  featureAccess: Nullable<{
    isAdmin: boolean;
    featureAccessList: {
      features: {
        featureName: string;
        branches: string[];
        users?: string[];
      }[];
    }[];
  }>;
  permissions: string[];
  userDetails?: {
    name: string;
  };
}

// KPI Dashboard State Types
export interface KpiDashboardState {
  branchList: Branch[];
  announcementsData: Announcement[];
  toDate: DateString;
  fromDate: DateString;
  selectedBranch: string;
  selectedTypes: string;
  dropdownLoader: LoadingState;
  kpiBranch: string;
}

// Jobs State Types
export interface CustomerInfoDrawer {
  drawer: boolean;
  loader: boolean;
}
export interface SchedulerInfoDrawer {
  drawer: boolean;
  loader: boolean;
}

export interface CheckboxValues {
  sms: boolean;
  email: boolean;
}

export interface CrewBuilderGroup {
  id: ID;
  name: string;
  members: string[];
}

export interface OneJob {
  crew: {
    drivers: Driver[];
    helpers: Helper[];
    trucks: Truck[];
    type: string;
  };
  jobs: Job[];
}

export interface JobsState {
  loading: LoadingState;
  searchLoading: LoadingState;
  jobsList: Nullable<JobsByCrewGroup[]>;
  jobsListJunk: Nullable<JobsByCrewGroup[]>;
  junkStatistics: Nullable<JunkStatistics>;
  uniqueCrewBuilderGroups: TransformedCrewDetailsForBuilder[];
  unScheduledJobsList: UnscheduledJob[];
  jobsListLoading: LoadingState;
  branchJobStats: Nullable<GenericObject>;
  jobSearchList: Job[];
  alertsList: Alert[];
  branchList: Branch[];
  currentBranch: Nullable<Branch>;
  error: ErrorState;
  reSizeWindow: boolean;
  accessibility: boolean;
  jobsWarning: Warning[];
  crewCardWarning: Warning[];
  calenderDate: Nullable<DateString>;
  jobDetailsData: GenericObject;
  customerInfo: CustomerInfoDrawer;
  scheduleInfo: SchedulerInfoDrawer;
  crewBuilderModal: boolean;
  crewBuilderDrawer: any;
  jobDetailsDataLoading: LoadingState;
  filteredList: Job[];
  confirmJobList: Job[];
  jobDetails: GenericObject;
  modalState: GenericObject;
  selectedFilter: string;
  customerInfoDetails: JobDetails | undefined;
  customerInfoDetailsCopy: JobDetails | undefined;
  laborTimesCustomerInfoDetails: GenericObject;
  detailsPayload: GenericObject;
  detailsPayloadCopu: GenericObject;
  saveModalOpen: boolean;
  modalKey: string;
  customerinfoMaterialsData: Nullable<GenericObject>;
  customerinfoLaborData: Nullable<GenericObject>;
  jobHistory: History[] | undefined;
  junkCheckboxValues: CheckboxValues;
  fvpCheckboxValues: CheckboxValues;
  junkSendInfo: Nullable<GenericObject>;
  paymentSummary: GenericObject;
  paymentFormSummary: GenericObject;
  paymentHistory: PaymentData[];
  paymentDates: DateString[];
  invoiceDetails: GenericObject;
  invoiceDetailsJunk: GenericObject;
  isProcessPayment: boolean;
  isProcessRefund: boolean;
  customerInfoForPayment: GenericObject;
  invoiceDetailsSummary: GenericObject;
  moveTypeList: MoveType[];
  globalJobId: string;
  selectJobId?: string;
  dragJobServiceType: string;
  dragJobId: string;
  dragJobSlot: string;
  tabIndex: number;
  dragJobBookedDate: DateString;
  addJobs: (job: Job) => void;
}

// Employee State Types
export interface Employee {
  _id: ID;
  days_available: string[];
  first_name: string;
  last_name: string;
  branch: Branch;
  contacts: any[];
  employee_address: Address;
  department: string;
  designation: string;
  compensation: any;
}

export interface PayrollItem {
  id: ID;
  employeeId: ID;
  period: string;
  amount: number;
}

export interface PayrollPeriod {
  id: ID;
  startDate: DateString;
  endDate: DateString;
  status: string;
}

export interface EmployeeState {
  error: ErrorState;
  itemListGet: boolean;
  employeeList: Employee[];
  payrollList: Nullable<{
    isBonusEnabled: boolean;
    isUpdatable: boolean;
    isSubmitted: boolean;
    isApproved: boolean;
    labourProfit: number;
    payrolls: PayrollItem[];
  }>;
  selectedDays: string[];
  employeeDetails: GenericObject;
  payrollDetails: GenericObject;
  branches: Branch[];
  selectedBranch: string;
  active: boolean;
  deductions?: {
    deductions: {
      id: ID;
      item: string;
      price: number;
      quantity: number;
      total: number;
    }[];
  };
  extraPay: {
    extra_pay: {
      id: ID;
      item: string;
      price: number;
      quantity: number;
      total: number;
      date: DateString;
      reason: string;
      time: string;
    }[];
    total_extra_pay_amount: number;
  };
  deductionsLoading: LoadingState;
  employeeListLoading: LoadingState;
  payrollListLoading: LoadingState;
  compensation_Type: SelectOption[];
  county: SelectOption[];
  department: SelectOption[];
  position_master: SelectOption[];
  state_master: SelectOption[];
  payrollPeriod?: {
    success: boolean;
    node?: {
      currentPeriod: string;
      previousWeek: string;
      p0: string;
      p1: string;
      p2: string;
      p3: string;
    };
  };
  selectedpayrollPeriod: string;
  payrollReportData: GenericObject;
  selectedEmployee: string;
  selectedPosition: string;
  payrollReportFillterdData: GenericObject;
}

// Customer Info Job History State Types
export interface CustomerInfoJobHistoryState {
  data: HistoryItem[];
  originalData: HistoryItem[];
}

// Customer Info State Types
export interface UserInfo {
  id?: string;
  fullName: string;
  status: string;
  phone?: string;
  email?: string;
  startTime: string;
  endTime: string;
  fromZone: string;
  toZone: string;
  startDate: string;
  endDate: string;
}

export interface MileInfo {
  Option: string;
  Bedrooms?: string;
  'Move Type'?: string;
  size: string;
  address?: string;
  address1?: string;
  address2?: string;
  sq?: string | number;
  weight?: string | number;
  floors?: string | number;
  longCarry?: string | number;
  date: string;
  city: string;
  state: string;
  zip?: string;
  zip_code?: string;
  country?: string;
  info?: any;
}

export interface CrewNotes {
  notes: string;
}

export interface CustomerInfoJobDetails {
  _id: string;
  customer: Customer;
  startTime: string;
  endTime: string;
  status: string;
  crew_notes: string;
  crew: { trucks: [] };
  crew_size: number;
  from_address: Address;
  additional_stops: MileInfo[];
  to_address: GenericObject;
  from_info: Info;
  to_info: Info;
  office_to_origin: number;
  origin_to_destination: number;
  destination_to_office: number;
  move_type: GenericObject;
  distance: GenericObject;
  move_date: string;
  slot: string;
  tags?: string[];
}

export interface CustomerInfoState {
  loading: LoadingState;
  error: ErrorState;
  prevUserInfo: GenericObject;
  prevCrewNotes?: CrewNotes;
  jobDetails: CustomerInfoJobDetails | {};
  toAddress: GenericObject;
  prevMilesToDestinatonInfo: GenericObject;
  _id: string;
  fromOffice: number;
  originToDestination: number;
  destinationToOffice: number;
  moveType: GenericObject;
  distance: GenericObject;
  slot: string;
  userInfo: UserInfo;
  prevMileInfo: MileInfo[];
  mileInfo: MileInfo[];
  milesToDestinatonInfo: MileInfo;
  crewDetails: GenericObject;
  crewSize: number;
  trucks: number;
  crewNotesInfo: CrewNotes;
}

// Crews State Types
export interface Driver {
  id: ID;
  name: string;
  licenseNumber: string;
  status: string;
  age: number;
}

export interface Helper {
  id: ID;
  name: string;
  status: string;
}

export interface Truck {
  id: ID;
  truckNumber: string;
  type: string;
  status: string;
}

export interface CrewsState {
  loading: LoadingState;
  driverList: Driver[];
  helperList: Helper[];
  truckList: Truck[];
  error: ErrorState;
}

export interface RVPName {
  _id: string;
  name: 'rvp';
  is_assigned: boolean;
}
export interface OMName {
  _id: string;
  name: 'om';
  is_assigned: boolean;
  external_id: number;
}

// Branch State Types
export interface BranchItem {
  id: ID;
  name: string;
  location?: string;
  om_name?: [OMName];
  city?: string;
  state?: string;
  phone?: string;
  ext?: string;
  is_auto_approve_rental_po: boolean;
  rvp_name?: [RVPName];
  brand?: string;
  isActive?: boolean;
  org_id?: {
    _id: string;
    name: string;
  };
}

export interface BranchList {
  getBranchList: BranchItem[];
}

export interface AuthorityEntry {
  stateCode: string;
  authorityNumber: string;
  default?: boolean;
}

export interface SelectedBranchLocation {
  cityName?: string;
  stateName?: string;
  branchName: string;
  orgId?: string;
  allAuthorities?: AuthorityEntry[];
}

export interface SpecialityItem {
  _id?: string | undefined;
  item_name?: string | undefined;
  number_of_men?: number | undefined;
  price?: number | undefined;
  range?: string | undefined;
}

export interface ServiceAreaItem {
  _id?: string | undefined;
  high_end?: boolean | undefined;
  major_city?: string | undefined;
  share?: boolean | undefined;
  state?: string | undefined;
  zip?: string | undefined;
  estimator_list?:
    | (
        | {
            _id: string;
            branch_name?: string | undefined;
            designation?: string | undefined;
            external_id?: number | undefined;
            full_name?: string | undefined;
            is_selected?: boolean | undefined;
          }
        | undefined
      )[]
    | undefined;
  estimator?:
    | {
        _id?: string | undefined;
        external_id?: number | undefined;
        name?: string | undefined;
      }
    | undefined;
  price_category?:
    | (
        | {
            is_selected?: boolean;
            value?: string;
          }
        | undefined
      )[]
    | undefined;
}

export interface BranchState {
  isBranchModal: boolean;
  branchList: BranchList;
  searchedBranchList: BranchItem[];
  specilityItemList: (SpecialityItem | undefined)[] | undefined;
  serviceAreaItemList: (ServiceAreaItem | undefined)[] | undefined;
  active: boolean;
  selectedBranch: string;
  selectedBranchLocation: SelectedBranchLocation;
}

// Scheduling State Types
export interface SchedulingState {
  currentGTId?: string;
  schedulerInfo: SchedulerInfoDrawer;
}

export interface CabType {
  id?: number | undefined;
  name?: string | undefined;
}

export interface FuelCapacity {
  id?: number | undefined;
  name?: number | undefined;
}

export interface FuelCardNumber {
  id?: string | undefined;
  name?: string | undefined;
}

export interface FuelType {
  id?: number | undefined;
  name?: string | undefined;
}

export interface RentalCompany {
  id?: number | undefined;
  name?: string | undefined;
}

export interface RentalReason {
  id?: number | undefined;
  name?: string | undefined;
}

export interface VehicleStatus {
  id?: number | undefined;
  name?: string | undefined;
}

export interface VehicleOrganisation {
  _id?: string | undefined;
  name?: string | undefined;
}

export interface RegistrationState {
  external_id?: number | undefined;
  name?: string | undefined;
  state_code?: string | undefined;
}

// Vehicle State Types
export interface VehicleState {
  vehicleCreated: boolean;
  vehicleGet: boolean;
  rentalGet: boolean;
  rentalCreated: boolean;
  itemListGet: boolean;
  vehicleInfo: GenericObject;
  rentalInfo: GenericObject;
  resourceDetails: {
    description?: string;
    nationalAccountVendors?: {
      vendor: string;
      contact: string;
      nra_number: string;
      options: string[];
    }[];
  };
  vehicleList: any;
  rentalList: any[];
  branchs: any[];
  companys: any[];
  cabType?: (CabType | undefined)[];
  fuelCapacity?: (FuelCapacity | undefined)[];
  fuelCardNumber?: (FuelCardNumber | undefined)[];
  fuelType?: (FuelType | undefined)[];
  registationState: RegistrationState[];
  organisations?: (VehicleOrganisation | undefined)[];
  rentalCompany?: (RentalCompany | undefined)[];
  rentalReasonNeeded?: (RentalReason | undefined)[];
  status?: (VehicleStatus | undefined)[];
  units: any[] | null;
  domain: string;
  token: string | null;
  vehicleStoreLoading: boolean;
  currentBranch: null;
  filter: {
    toggle: boolean;
    branch: string;
    branchId: string;
    company: string;
    companyId: string;
    date: string;
  };
}

export type BranchDetails = {
  name?: string;
};

export type TruckBranch = {
  external_id?: number;
  name?: string;
};

export type RentalDetails = {
  _id: string;
  agreement_number?: string;
  company_id?: string;
  company_name?: string;
  date_in?: string;
  date_out?: string;
  end_date?: Date;
  expected_out?: string;
  miles_in?: number;
  miles_out?: number;
  mongoDBId?: string;
  reason_needed?: string;
  rental_company?: string;
  rental_docs?: InsuranceOrRentalOrServiceDocument;
  rental_reason?: string;
  start_date?: Date;
  status?: string;
  status_code?: boolean;
  total_miles?: number;
  vehicle_number?: string;
};

export type ServiceHistory = {
  service_company?: string;
  service_date?: string;
  service_details?: string;
  service_docs?: (InsuranceOrRentalOrServiceDocument | undefined)[];
};

export type TruckOrg = {
  _id?: string;
  name?: string;
};

export type MoveType = {
  id?: number;
  name: string;
};

export type Address = {
  address1?: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  country?: string;
};

export type History = {
  call_result?: string;
  comments?: string;
  created_by?: string;
  date_time?: string;
  description?: string;
  employee_id?: number;
  method?: string;
  new_move_workflow_step?: string;
  previous_move_workflow_step?: string;
  title?: string;
};

export type AuditFields = {
  created_at?: Date;
  created_by?: string;
  updated_at?: Date;
  updated_by?: string;
};

export type AuditFieldsNotTyped = {
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
};

export type InsuranceOrRentalOrServiceDocument = {
  file_name?: string;
  file_type?: string;
};

export type InsuranceDetails = {
  end_date?: Date;
  insurance_company_name?: string;
  insurance_docs?: (InsuranceOrRentalOrServiceDocument | undefined)[];
  insured_amount?: number;
  premium_amount?: number;
  start_date?: Date;
  status?: string;
};

export type RegistrationRenewal = {
  due_at?: Date;
  last_renewed_date?: Date;
  status?: string;
};

export type JobsByCrewTruck = {
  _id?: string;
  additional_hourly_rate?: number;
  audit_fields?: AuditFields;
  branch?: TruckBranch;
  insurance_details?: InsuranceDetails;
  insurance_status?: string;
  is_disabled?: boolean;
  is_empty?: boolean;
  manufacturer?: string;
  mileage?: number;
  model?: string;
  name?: string;
  org_id?: TruckOrg;
  reg_no?: string;
  registration_no?: string;
  registration_renewal?: RegistrationRenewal;
  rental_details?: RentalDetails[];
  service_history?: ServiceHistory[];
  status?: string;
  truck_id?: string;
  year?: number;
  hourly_rate?: number;
};

export const ContactType = {
  claims: 'claims',
  dispatch: 'dispatch',
  email: 'email',
  marketing: 'marketing',
  phone: 'phone',
  quick: 'quick',
  sales: 'sales',
} as const;

export type ContactType = (typeof ContactType)[keyof typeof ContactType];

export type Contact = {
  ext?: string;
  primary?: boolean;
  type: ContactType;
  value: string;
};

export type DrugTestVariant = {
  status?: boolean;
  test_date?: string;
};

export type DrugTest = {
  drug_test?: DrugTestVariant;
};

export type DriverLicense = {
  expiry_date?: string;
  issued_date?: string;
  license_number?: string;
  number?: string;
};

export type JobsByCrewEmployee = {
  _id?: string;
  additional_hourly_rate?: number;
  age?: number;
  audit_fields?: AuditFields;
  bgv?: DrugTest;
  branch?: string;
  cdl_info?: string;
  closing_ratio_color?: string;
  contacts?: Contact[];
  department?: string;
  designation?: string;
  dob?: string;
  doj?: string;
  driver_license?: DriverLicense;
  employee_id?: string;
  first_name?: string;
  gc_device_id?: string;
  hourly_rate?: number;
  is_disabled?: boolean;
  is_empty?: boolean;
  last_name?: string;
  middle_name?: string;
  mobile_os?: string;
  name?: string;
  reports_to?: string;
  ssn?: string;
  status?: string;
  truck_id?: string;
};

export type JobsByCrew = {
  drivers?: (JobsByCrewEmployee | undefined)[];
  helpers?: (JobsByCrewEmployee | undefined)[];
  trucks?: (JobsByCrewTruck | undefined)[];
  type?: string;
  minimum_trucks?: number;
  uuid?: string;
};

export const DistanceUnit = {
  km: 'km',
  miles: 'miles',
} as const;

export type DistanceUnit = (typeof DistanceUnit)[keyof typeof DistanceUnit];

export type JobDistance = {
  unit: DistanceUnit;
  value: number;
};

export type ToOrFromInfoMoveSize = {
  service?: string;
  type?: string;
  value?: string;
  vault?: number;
};

export type ToOrFromInfo = {
  cube?: number;
  delivery?: boolean;
  elevator?: boolean;
  floors?: number;
  lat?: number;
  lc?: string;
  long?: number;
  move_size?: ToOrFromInfoMoveSize;
  pickup?: boolean;
  sqft?: number;
  stairs?: boolean;
  unit?: string;
};

export type JobStop = {
  address1?: string;
  address2?: string;
  city?: string;
  distance?: JobDistance;
  info?: ToOrFromInfo;
  state?: string;
  stop?: number;
  zip_code: string;
};

export type JobBranch = {
  external_id?: number;
  name?: string;
};

export type JobCustomer = {
  fvp?: boolean;
  name?: string;
  vip?: boolean;
};

export type BolFLagDocuments = {
  file_name?: string;
  type?: string;
};

export type Bol = {
  customer_date?: string;
  customer_time?: string;
  driver_date?: string;
  driver_time?: string;
  pdf_type?: string;
  title?: string;
};

export type Profile = {
  email?: string;
  name?: string;
  phone_number?: string;
  po?: string;
  type?: string;
};

export type DiscountDetail = {
  type?: string;
  value?: number;
};

export type Discount = {
  coupon?: DiscountDetail;
  percentage?: DiscountDetail;
};

export const FuelChargeType = {
  flat: 'flat',
  percent: 'percent',
} as const;

export type FuelChargeType = (typeof FuelChargeType)[keyof typeof FuelChargeType];

export type FuelCharge = {
  type?: FuelChargeType;
  value?: number;
};

export type InvoiceSummary = {
  additional_charges_amount?: number;
  fvp_charge_amount?: number;
  packing_materials_amount?: number;
  specialty_materials_amount?: number;
};

export type JobRevenue = {
  amount?: number;
  is_flat_rate?: boolean;
};

export type MoveSizePricing = {
  above_250m_price?: number;
  below_50m_price?: number;
  between_50_to_250m_price?: number;
  external_id?: number;
  move_size?: string;
};

export type LaborTimeCrewMember = {
  down_sell_hourly_rate?: number;
  employee_id: string;
  first_name?: string;
  is_disabled?: boolean;
  is_driver?: boolean;
  labor_hours?: number;
  last_name?: string;
  non_labor_hours?: number;
  removal_reason?: string;
  travel_hours?: number;
};

export type LaborAddress = {
  address1?: string;
  address2?: string;
  city: string;
  county?: string;
  state: string;
  zip_code: string;
};

export type LaborTime = {
  address?: LaborAddress;
  break_time?: number;
  crew?: LaborTimeCrewMember[];
  duration?: number;
  finish_time?: string;
  hourly_rate?: number;
  is_default?: boolean;
  is_overtime?: boolean;
  is_overtime_checked?: boolean;
  miles?: number;
  service_type?: string;
  start_time?: string;
  task_type?: string;
  travel_hours?: number;
};

export type FlatRateData = {
  flat_rate_type?: string;
};

export type EmailSentStatus = {
  avp?: boolean;
  creditCardAuthorization?: boolean;
  moveQuote?: boolean;
  unableToConfirm?: boolean;
};

export type JobMetadataDetailsByCrew = {
  actual_hourly_rate?: number;
  actual_minimum_crew?: number;
  amount?: number;
  bell_end_time?: string;
  bell_start_time?: string;
  bol_status?: Bol[];
  booked_date?: string;
  call_result?: string;
  company_profiles?: Profile[];
  completed_date?: string;
  flat_rate_data?: FlatRateData;
  customer_profiles?: Profile[];
  customer_sign?: string;
  discount?: Discount;
  down_sell_hourly_rate?: number;
  est_cost?: number;
  est_date?: string;
  estimate_types?: string[];
  estimator_type?: string;
  fuel_charge?: FuelCharge;
  hourly_rate?: number;
  rateChangeReason?: string;
  invoice_summary?: InvoiceSummary;
  is_junk?: boolean;
  job_revenue?: JobRevenue;
  junk_rate?: number;
  junk_truck_needed?: boolean;
  labor_times?: LaborTime[];
  labour_hours?: number;
  lead_creation_date?: string;
  minimum_crew?: number;
  minimum_hours?: number;
  minimum_travel_crew?: number;
  move_dates?: string[];
  move_id?: number;
  move_pricing?: MoveSizePricing[];
  pdf_path?: string;
  promo_code?: number;
  scope?: string;
  special_notes?: string;
  storage?: string;
  total_charges?: number;
  travel_hours?: number;
  isRegulatedHourly?: boolean;
  isRegulatedWeightAndDistance?: boolean;
  emailSentStatus?: EmailSentStatus;
  isBOLPdfPresent?: boolean;
  source?: string;
};

export type CrewTruck = {
  additional_hourly_rate?: number;
  hours_adjusted?: number;
  is_empty?: boolean;
  reason?: string;
  reg_no?: string;
  truck_id: string;
};

export type CrewMember = {
  additional_hourly_rate?: number;
  employee_id: string;
  hours_adjusted?: number;
  is_disabled?: boolean;
  is_empty?: boolean;
  name?: string;
  reason?: string;
  truck_id: string;
};

export type Crew = {
  drivers?: CrewMember[];
  helpers?: CrewMember[];
  trucks?: CrewTruck[];
};

export type JobDetailsByCrew = {
  _id?: string;
  additional_stops?: JobStop[];
  audit_fields?: AuditFieldsNotTyped;
  branchRequestedFrom?: string;
  branchRequestedTo?: string;
  branch_details?: JobBranch;
  crew?: Crew;
  crew_notes?: string;
  crew_size?: number;
  cube?: number;
  customer?: JobCustomer;
  description?: string;
  destination_duration?: number;
  destination_miles?: number;
  distance?: JobDistance;
  documents?: BolFLagDocuments[];
  endTime?: string;
  estimated?: boolean;
  external_id?: number;
  floors?: number;
  from_address?: Address;
  from_info?: ToOrFromInfo;
  history?: History[];
  hourly_rate?: number;
  intercompanyCrew?: number;
  internal_notes?: string;
  isIntercompany?: boolean;
  is_multi_day?: boolean;
  is_protected?: boolean;
  job_endtime?: string;
  job_starttime?: string;
  meta_data?: JobMetadataDetailsByCrew;
  minimum_crew?: number;
  minimum_trucks?: number;
  minutes?: number;
  move_type?: MoveType;
  revenue?: number;
  service_type?: string;
  slot?: string;
  source_duration?: number;
  source_miles?: number;
  sqft?: number;
  startTime?: string;
  status?: string;
  tags?: string[];
  partners?: string[];
  time_slot?: string;
  title?: string;
  to_address?: Address;
  to_info?: ToOrFromInfo;
  type?: string;
  weight?: number;
  move_dates?: string[];
  bol_status?: Bol[];
  move_date?: string;
};

export type JobDetailsByCrewExtended = JobDetailsByCrew & {
  newStartTime?: string;
  newEndTime?: string;
  placeholder?: boolean;
  lineHeight?: number;
  lineHeightInitial?: number;
  text?: string;
};

export type JobsByCrewGroup = {
  crew?: JobsByCrew;
  jobs?: (JobDetailsByCrew | undefined)[];
};

export type JobMetadata = {
  minimum_travel_crew?: number;
  labour_hours?: number;
  travel_hours?: number;
  minimum_hours?: number;
  fuel_charge?: FuelCharge;
  job_revenue?: JobRevenue;
  total_charges?: number;
  hourly_rate?: number;
  minimum_crew?: number;
  amount?: number;
  discount?: Discount;
  booked_date?: string;
  est_date?: string;
  invoice_summary?: InvoiceSummary;
  call_result?: string;
  down_sell_hourly_rate?: number;
  move_dates?: string[];
  lead_creation_date?: string;
  estimator_type?: string;
  estimate_types?: string[];
  est_cost?: number;
  completed_date?: string;
  actual_hourly_rate?: number;
  actual_minimum_crew?: number;
  actual_minimum_trucks?: number;
  move_pricing?: [MoveSizePricing];
  customer_sign?: string;
  pdf_path?: string;
  move_id?: number;
  company_profiles?: Profile[];
  customer_profiles?: Profile[];
  is_junk?: boolean;
  junk_rate?: number;
  promo_code?: number;
  junk_truck_needed?: boolean;
  storage?: string;
  scope?: string;
  bell_start_time?: string;
  bell_end_time?: string;
  special_notes?: string;
  labor_times?: LaborTime[];
  bol_status?: Bol[];
  isRegulatedHourly?: boolean;
  isRegulatedWeightAndDistance?: boolean;
  loadedWeight?: number;
  unloadedWeight?: number;
  flat_rate_data?: FlatRateData;
  isFullPack?: boolean;
  isBOLPdfPresent?: boolean;
  source?: string;
  isCdsRate?: boolean;
};

export type JobDetails = {
  _id?: ID;
  title?: string;
  customer?: Customer;
  crew_notes?: string;
  internal_notes?: string;
  minimum_trucks?: number;
  description?: string;
  type?: string;
  crew?: Crew;
  crew_size?: number;
  minutes?: number;
  startTime?: string;
  move_date?: string;
  slot?: string;
  estimated?: boolean;
  external_id?: number;
  sqft?: number;
  cube?: number;
  floors?: number;
  distance?: JobDistance;
  move_type?: MoveType;
  endTime?: string;
  branch_details?: BranchDetails;
  status?: string;
  history?: History[];
  to_info?: ToOrFromInfo;
  from_info?: ToOrFromInfo;
  revenue?: number;
  from_address?: Address;
  additional_stops?: JobStop[];
  to_address?: Address;
  audit_fields?: AuditFields;
  is_protected?: boolean;
  weight?: number;
  office_to_origin?: number;
  origin_to_destination?: number;
  destination_to_office?: number;
  tags?: string[];
  partners?: string[];
  minimum_crew?: number;
  hourly_rate?: number;
  meta_data?: JobMetadata;
  total_hours?: number;
  additional_hourly_rate?: number;
  message?: string;
  service_type?: string;
  is_multi_day?: boolean;
  time_slot?: string;
  isIntercompany?: boolean;
  intercompanyCrew?: number;
  intercompanyBranchRequestedTo?: string;
  intercompanyBranchRequestedFrom?: string;
  isRegulatedMove?: string;
  isRegulatedHourlyMove?: boolean;
  parentJob?: Dates;
  canUpdateHourlyRate?: boolean;
};

export type Dates = {
  job_id?: ID;
  move_date?: string;
};

export type TransformedCrewDetailsForBuilder = JobsByCrewEmployee & {
  helpers?: (JobsByCrewEmployee | undefined)[];
  truck?: JobsByCrewTruck | undefined;
  driver?: JobsByCrewEmployee | undefined;
  slotValues?: (string | undefined)[];
};

export type JunkStatistics = {
  completed: number;
  active: number;
  total: number;
};

export type UnscheduledJob = {
  _id: string;
  slot: string;
  move_date: string;
  minimum_trucks: number;
  minimum_crew: number;
  type: string;
  crew_size: number;
  minutes: number;
  status: string;
  revenue: number;
  is_protected: boolean;
  weight: number;
  tags: string[];
  external_id: number;
  service_type: string;
  meta_data?: {
    minimum_crew: number;
    junk_truck_needed: boolean;
    special_notes: string;
    actual_minimum_crew?: number;
  };
  additional_stops: JobStop[];
  from_info: ToOrFromInfo;
  move_type: MoveType;
  customer: {
    first_name: string;
    last_name: string;
  };
  title: string;
  branch_details: BranchDetails;
  from_address: Address;
  to_address: Address;
};

// Root State Interface
export interface RootState {
  customerInfo: CustomerInfoState;
  user: UserState;
  crew: CrewsState;
  jobs: JobsState;
  employee: EmployeeState;
  branch: BranchState;
  vehicle: VehicleState;
  jobHistory: CustomerInfoJobHistoryState;
  kpiBucket: KpiDashboardState;
  scheduling: SchedulingState;
}

export type StoreState = RootState;
